import React, { useEffect, useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import ConfigService from "../services/ConfigService";
import "./GeasFaq.css";

/**
 * FAQ (Domande Frequenti) specifiche per il tenant GEAS.
 *
 * Il componente è auto-contenuto: carica la configurazione, si mostra
 * SOLO quando il tenant attivo è GEAS (branding.name === "GEAS") e altrimenti
 * non renderizza nulla. Va inserito nelle pagine di Login e Registrazione.
 *
 * I testi sono adattati dal vecchio sito Health.NET Web (LogIn.aspx) alla
 * nuova piattaforma. Differenze principali rispetto al vecchio portale:
 *  - Reset password: link via email per reimpostarla (non più password in chiaro).
 *  - Blocco account: 5 tentativi -> blocco temporaneo di 15 minuti (auto-sblocco).
 *  - Primo accesso: registrazione dal portale o attivazione in accettazione,
 *    seguita dalla conferma dell'indirizzo email.
 */
const GeasFaq: React.FC = () => {
  const [isGeas, setIsGeas] = useState<boolean>(false);
  const [supportPhone, setSupportPhone] = useState<string>("");
  const [supportEmail, setSupportEmail] = useState<string>("");

  useEffect(() => {
    let mounted = true;
    const loadConfig = async () => {
      await ConfigService.loadConfig();
      const config = ConfigService.getConfig();
      if (!mounted || !config) return;
      setIsGeas((config.branding?.name || "").trim().toUpperCase() === "GEAS");
      setSupportPhone(config.address1?.telephone || "");
      setSupportEmail(config.support?.email || "");
    };
    loadConfig();
    return () => {
      mounted = false;
    };
  }, []);

  // Mostra le FAQ solo per il tenant GEAS
  if (!isGeas) return null;

  return (
    <div className="geas-faq">
      <h4 className="geas-faq-title">Domande frequenti</h4>
      <Accordion className="info-accordion" alwaysOpen>
        <Accordion.Item eventKey="geas-0">
          <Accordion.Header>Come posso accedere al servizio?</Accordion.Header>
          <Accordion.Body>
            <p>
              L'accesso ai referti on-line è riservato ai pazienti registrati. Puoi
              richiedere l'attivazione del servizio al personale dell'accettazione al
              momento dell'esecuzione della prestazione, oppure registrarti
              direttamente dal portale tramite l'apposito modulo di registrazione.
            </p>
            <p>
              Al termine della registrazione riceverai un'email per{" "}
              <strong>confermare il tuo indirizzo di posta</strong>: è un passaggio
              obbligatorio prima di poter accedere. Il tuo <strong>username</strong>{" "}
              corrisponde sempre al tuo <strong>Codice Fiscale</strong>.
            </p>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="geas-1">
          <Accordion.Header>
            Ho dimenticato la password: cosa devo fare?
          </Accordion.Header>
          <Accordion.Body>
            <p>
              L'username corrisponde al Codice Fiscale del paziente titolare delle
              analisi. Se hai smarrito la password, dalla pagina di accesso clicca su{" "}
              <strong>"Password dimenticata?"</strong> e inserisci il tuo Codice
              Fiscale.
            </p>
            <p>
              Riceverai all'indirizzo email registrato un messaggio con un{" "}
              <strong>link per reimpostare la password</strong>: seguendo il link
              potrai scegliere tu stesso una nuova password. Se richiedi più volte il
              reset, per sicurezza è valido <strong>solo l'ultimo link ricevuto</strong>;
              i link delle email precedenti vengono automaticamente disattivati.
            </p>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="geas-2">
          <Accordion.Header>
            Non riesco ad accedere alle analisi di un familiare: cosa devo fare?
          </Accordion.Header>
          <Accordion.Body>
            <p>
              Ogni paziente ha credenziali personali: l'username corrisponde al Codice
              Fiscale del paziente titolare delle analisi, cioè della persona che ha
              eseguito gli esami, non di chi li sta consultando per suo conto.
            </p>
            <p>
              La stessa email può essere utilizzata per più pazienti, ma ogni account
              ha una password distinta e personale. Per accedere alle analisi di un
              familiare è quindi necessario inserire il <strong>Codice Fiscale del
              familiare</strong> come username e la <strong>sua password personale</strong>.
            </p>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="geas-3">
          <Accordion.Header>
            Non ho ricevuto l'email di conferma o le credenziali: cosa devo fare?
          </Accordion.Header>
          <Accordion.Body>
            <p>
              Dopo la registrazione il sistema invia automaticamente un'email
              all'indirizzo che hai indicato. Se non la ricevi, verifica innanzitutto
              che l'indirizzo email sia corretto: errori di digitazione o indirizzi
              inesistenti impediscono la ricezione.
            </p>
            <p>
              Se l'email non è nella posta in arrivo, controlla la cartella{" "}
              <strong>SPAM</strong> o <strong>Posta indesiderata</strong> accedendo dal
              portale web della tua casella (es. Gmail, Libero, Yahoo): la cartella
              spam vista dal telefono potrebbe non coincidere con quella del portale.
            </p>
            <p>
              Puoi comunque richiedere un nuovo invio dell'email di conferma tramite la
              funzione <strong>"Reinvia email di conferma"</strong> presente nella
              pagina di accesso. Se il problema persiste, contatta la segreteria del
              Laboratorio.
            </p>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="geas-4">
          <Accordion.Header>
            Ho la password ma non riesco a entrare: cosa devo fare?
          </Accordion.Header>
          <Accordion.Body>
            <p>
              Se stai copiando la password da un'email, incollala direttamente senza
              riscriverla a mano: alcuni caratteri si prestano a confusione visiva,
              come la I maiuscola e la l minuscola, oppure lo zero (0) e la lettera O.
            </p>
            <p>
              <strong>Attenzione:</strong> dopo <strong>5 tentativi</strong> di accesso
              con password errata l'account viene bloccato automaticamente per{" "}
              <strong>15 minuti</strong>. Trascorso questo tempo l'account si sblocca da
              solo e potrai riprovare. Se non ricordi la password, puoi reimpostarla
              subito con <strong>"Password dimenticata?"</strong> senza attendere.
            </p>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="geas-5">
          <Accordion.Header>
            Il sistema resta sulla schermata di accesso e non mi fa entrare
          </Accordion.Header>
          <Accordion.Body>
            <p>
              Se la pagina rimane sull'accesso senza mostrare messaggi, le cause più
              comuni sono due:
            </p>
            <p>
              1. <strong>Email non ancora confermata</strong>: finché non confermi
              l'indirizzo email tramite il link ricevuto non è possibile accedere. Usa
              la funzione "Reinvia email di conferma" se non trovi il messaggio.
            </p>
            <p>
              2. <strong>Account temporaneamente bloccato</strong> a seguito di troppi
              tentativi errati (5): in questo caso è sufficiente attendere 15 minuti,
              oppure reimpostare la password con "Password dimenticata?".
            </p>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="geas-6">
          <Accordion.Header>
            Il referto on-line sostituisce il formato cartaceo?
          </Accordion.Header>
          <Accordion.Body>
            <p>
              Sì. I referti disponibili sul portale sono firmati elettronicamente dal
              Direttore Tecnico e hanno pertanto piena validità legale, in conformità
              con quanto richiesto dalla Regione Lazio per l'inserimento nel Fascicolo
              Sanitario Elettronico (FSE). Il referto on-line include tutti gli
              allegati e sostituisce a tutti gli effetti il documento cartaceo.
            </p>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="geas-7">
          <Accordion.Header>
            Ho richiesto il servizio on-line: posso comunque ritirare le analisi in
            formato cartaceo?
          </Accordion.Header>
          <Accordion.Body>
            <p>
              Chi aderisce al servizio di consultazione on-line riceve i referti
              esclusivamente in formato digitale, resi disponibili direttamente sul
              portale.
            </p>
            <p>
              È comunque possibile richiedere anche la stampa cartacea dei referti,{" "}
              <strong>ma la richiesta deve essere effettuata al momento
              dell'accettazione</strong>. Ti invitiamo pertanto a valutare
              attentamente la tua scelta al momento della registrazione.
            </p>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {(supportPhone || supportEmail) && (
        <p className="geas-faq-contacts">
          Per qualsiasi richiesta di supporto puoi contattare il Laboratorio
          {supportPhone && (
            <>
              {" "}al numero <a href={`tel:${supportPhone}`}>{supportPhone}</a>
            </>
          )}
          {supportPhone && supportEmail ? " o" : ""}
          {supportEmail && (
            <>
              {" "}via email all'indirizzo{" "}
              <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
            </>
          )}
          .
        </p>
      )}
    </div>
  );
};

export default GeasFaq;
